/**
 * Rate Validation Service
 * Provides validation logic for rate master data including overlap detection
 */

import { ValidationUtils } from '../types';

export class RateValidationService {
  /**
   * Validates a rate for overlapping date ranges with existing rates
   * @param {Object} rateData - Rate data to validate
   * @param {Array} existingRates - Array of existing rates
   * @param {string} excludeId - ID to exclude from validation (for updates)
   * @returns {Object} Validation result with isValid flag and errors
   */
  static validateRateOverlap(rateData, existingRates, excludeId = null) {
    const errors = [];

    // Filter rates for the same cost category, excluding the current rate if updating
    const categoryRates = existingRates.filter((rate) => rate.costCategory === rateData.costCategory
      && rate.isActive
      && ((!excludeId) || (rate.id !== excludeId)));

    // Check for overlaps
    const newRange = {
      startDate: new Date(rateData.effectiveFrom),
      endDate: new Date(rateData.effectiveTo),
    };

    const overlappingRates = categoryRates.filter((rate) => {
      const existingRange = {
        startDate: new Date(rate.effectiveFrom),
        endDate: new Date(rate.effectiveTo),
      };
      return ValidationUtils.doDateRangesOverlap(newRange, existingRange);
    });

    if (overlappingRates.length > 0) {
      overlappingRates.forEach((rate) => {
        errors.push({
          type: 'OVERLAP_ERROR',
          message: `Rate period overlaps with existing rate (ID: ${rate.id}) `
            + `from ${this.formatDate(rate.effectiveFrom)} `
            + `to ${this.formatDate(rate.effectiveTo)}`,
          overlappingRate: rate,
        });
      });
    }

    return {
      isValid: errors.length === 0,
      errors,
      overlappingRates,
    };
  }

  /**
   * Validates rate data completeness and business rules
   * @param {Object} rateData - Rate data to validate
   * @returns {Object} Validation result
   */
  static validateRateData(rateData) {
    const errors = [];

    // Required fields validation
    if (!rateData.costCategory) {
      errors.push({
        type: 'REQUIRED_FIELD',
        field: 'costCategory',
        message: 'Cost category is required',
      });
    }

    if (rateData.rate === null || rateData.rate === undefined || rateData.rate === '') {
      errors.push({
        type: 'REQUIRED_FIELD',
        field: 'rate',
        message: 'Rate is required',
      });
    }

    if (!rateData.rateType) {
      errors.push({
        type: 'REQUIRED_FIELD',
        field: 'rateType',
        message: 'Rate type is required',
      });
    }

    if (!rateData.effectiveFrom) {
      errors.push({
        type: 'REQUIRED_FIELD',
        field: 'effectiveFrom',
        message: 'Effective from date is required',
      });
    }

    if (!rateData.effectiveTo) {
      errors.push({
        type: 'REQUIRED_FIELD',
        field: 'effectiveTo',
        message: 'Effective to date is required',
      });
    }

    // Business rules validation
    if (rateData.rate !== null && rateData.rate !== undefined) {
      const rate = parseFloat(rateData.rate);

      if (Number.isNaN(rate)) {
        errors.push({
          type: 'INVALID_VALUE',
          field: 'rate',
          message: 'Rate must be a valid number',
        });
      } else if (rate < 0) {
        errors.push({
          type: 'INVALID_VALUE',
          field: 'rate',
          message: 'Rate must be non-negative',
        });
      } else if (rateData.rateType === 'PERCENTAGE' && rate > 100) {
        errors.push({
          type: 'INVALID_VALUE',
          field: 'rate',
          message: 'Percentage rate cannot exceed 100%',
        });
      }
    }

    // Date validation
    if (rateData.effectiveFrom && rateData.effectiveTo) {
      const fromDate = new Date(rateData.effectiveFrom);
      const toDate = new Date(rateData.effectiveTo);

      if (Number.isNaN(fromDate.getTime())) {
        errors.push({
          type: 'INVALID_DATE',
          field: 'effectiveFrom',
          message: 'Effective from date is invalid',
        });
      }

      if (Number.isNaN(toDate.getTime())) {
        errors.push({
          type: 'INVALID_DATE',
          field: 'effectiveTo',
          message: 'Effective to date is invalid',
        });
      }

      if (
        !Number.isNaN(fromDate.getTime())
        && !Number.isNaN(toDate.getTime())
        && fromDate >= toDate
      ) {
        errors.push({
          type: 'INVALID_DATE_RANGE',
          field: 'effectiveTo',
          message: 'Effective to date must be after effective from date',
        });
      }
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  /**
   * Comprehensive validation combining data validation and overlap checking
   * @param {Object} rateData - Rate data to validate
   * @param {Array} existingRates - Array of existing rates
   * @param {string} excludeId - ID to exclude from validation (for updates)
   * @returns {Object} Complete validation result
   */
  static validateRate(rateData, existingRates, excludeId = null) {
    const dataValidation = this.validateRateData(rateData);

    if (!dataValidation.isValid) {
      return dataValidation;
    }

    const overlapValidation = this.validateRateOverlap(rateData, existingRates, excludeId);

    return {
      isValid: dataValidation.isValid && overlapValidation.isValid,
      errors: [...dataValidation.errors, ...overlapValidation.errors],
      overlappingRates: overlapValidation.overlappingRates || [],
    };
  }

  /**
   * Finds gaps in rate coverage for a cost category
   * @param {string} costCategory - Cost category to check
   * @param {Array} rates - Array of rates for the category
   * @param {Date} startDate - Start date for gap analysis
   * @param {Date} endDate - End date for gap analysis
   * @returns {Array} Array of date ranges with no coverage
   */
  static findRateGaps(costCategory, rates, startDate, endDate) {
    const categoryRates = rates
      .filter((rate) => rate.costCategory === costCategory && rate.isActive)
      .sort((a, b) => new Date(a.effectiveFrom) - new Date(b.effectiveFrom));

    const gaps = [];
    let currentDate = new Date(startDate);
    const analysisEndDate = new Date(endDate);

    categoryRates.forEach((rate) => {
      const rateStart = new Date(rate.effectiveFrom);
      const rateEnd = new Date(rate.effectiveTo);

      // Check if there's a gap before this rate
      if (currentDate < rateStart) {
        gaps.push({
          startDate: new Date(currentDate),
          endDate: new Date(Math.min(rateStart.getTime() - 1, analysisEndDate.getTime())),
          type: 'NO_COVERAGE',
        });
      }

      // Move current date to after this rate
      currentDate = new Date(Math.max(currentDate.getTime(), rateEnd.getTime() + 1));

      // If we've covered the entire analysis period, break
      if (currentDate > analysisEndDate) {
        currentDate = new Date(analysisEndDate.getTime() + 1);
      }
    });

    // Check if there's a gap after the last rate
    if (currentDate <= analysisEndDate) {
      gaps.push({
        startDate: new Date(currentDate),
        endDate: new Date(analysisEndDate),
        type: 'NO_COVERAGE',
      });
    }

    return gaps;
  }

  /**
   * Analyzes rate history for a cost category
   * @param {string} costCategory - Cost category to analyze
   * @param {Array} rates - Array of all rates
   * @returns {Object} Analysis result with statistics and issues
   */
  static analyzeRateHistory(costCategory, rates) {
    const categoryRates = rates
      .filter((rate) => rate.costCategory === costCategory)
      .sort((a, b) => new Date(a.effectiveFrom) - new Date(b.effectiveFrom));

    const analysis = {
      totalRates: categoryRates.length,
      activeRates: categoryRates.filter((rate) => rate.isActive).length,
      inactiveRates: categoryRates.filter((rate) => !rate.isActive).length,
      overlaps: [],
      gaps: [],
      rateChanges: [],
    };

    // Find overlaps
    categoryRates.forEach((rate1, i) => {
      categoryRates.slice(i + 1).forEach((rate2) => {
        if (ValidationUtils.doDateRangesOverlap(
          { startDate: new Date(rate1.effectiveFrom), endDate: new Date(rate1.effectiveTo) },
          { startDate: new Date(rate2.effectiveFrom), endDate: new Date(rate2.effectiveTo) },
        )) {
          analysis.overlaps.push({
            rate1,
            rate2,
            overlapStart: new Date(Math.max(
              new Date(rate1.effectiveFrom).getTime(),
              new Date(rate2.effectiveFrom).getTime(),
            )),
            overlapEnd: new Date(Math.min(
              new Date(rate1.effectiveTo).getTime(),
              new Date(rate2.effectiveTo).getTime(),
            )),
          });
        }
      });
    });

    // Calculate rate changes
    categoryRates.slice(1).forEach((currentRate, index) => {
      const previousRate = categoryRates[index];

      const change = currentRate.rate - previousRate.rate;
      const percentChange = (change / previousRate.rate) * 100;

      analysis.rateChanges.push({
        fromRate: previousRate,
        toRate: currentRate,
        change,
        percentChange,
        changeDate: new Date(currentRate.effectiveFrom),
      });
    });

    return analysis;
  }

  /**
   * Utility method to format dates consistently
   * @param {Date|string} date - Date to format
   * @returns {string} Formatted date string
   */
  static formatDate(date) {
    if (!date) return '';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }
}

export default RateValidationService;
